import { Button, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ReactComponent as ReplaceIcon } from 'assets/icons/toolbar/replace.svg';
import { BUTTON_SIZE, FONT_SIZE, ICON_SIZE } from 'modules/Editor/components/WidgetControl/WidgetControl.config';
import { RootState, useAppDispatch, useAppSelector, useAppStore } from 'modules/Editor/store';
import { selectActiveWidgetMenu, selectIsFlowModeEnabled } from 'modules/Editor/store/editorSettingsSelector';
import { setActiveWidgetMenu, setReplaceWidgetMenuIndex } from 'modules/Editor/store/editorSettingsSlice';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import {
  selectActiveWidgetType,
  selectActiveWidgets,
  selectHasReplaceButton,
} from 'modules/Editor/store/widgetSelector';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { WidgetType } from 'types/widget.types';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

export const ImageWidgetCustomControl = () => {
  const { t } = useTranslation('editor_toolbar');
  const dispatch = useAppDispatch();

  const activeWidgetType = useAppSelector(selectActiveWidgetType);
  const showReplace = useAppSelector(selectHasReplaceButton);
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const { id, groupId, responsiveGroupId } = activeWidgets[0];
  const isFlowModeEnabled = useAppSelector(selectIsFlowModeEnabled);
  const isFlowWidget = lineConnectionCache.isConnected(groupId || responsiveGroupId || id);
  const store = useAppStore();

  const showAsIcon = useMemo(() => {
    return isFlowModeEnabled || isFlowWidget;
  }, [isFlowModeEnabled, isFlowWidget]);

  const onClick = () => {
    const state = store.getState() as RootState;
    const activeWidgetMenu = selectActiveWidgetMenu(state);

    let widgetMenuIndex = ReplaceMenuTabIndex.UPLOAD;

    if (activeWidgetType === WidgetType.Icon) {
      widgetMenuIndex = ReplaceMenuTabIndex.ICON;
    }

    if (activeWidgetMenu !== WIDGET_MENU_OPTIONS.REPLACE) {
      dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.REPLACE));
    }

    dispatch(setReplaceWidgetMenuIndex(widgetMenuIndex));
  };

  const label = t('replaceBtn.label');

  if (!showReplace) {
    return null;
  }

  if (showAsIcon) {
    return (
      <Tooltip hasArrow placement='bottom' label={label} bg='black'>
        <IconButton
          aria-label={label}
          color='font.500'
          width={`${BUTTON_SIZE}px`}
          height={`${BUTTON_SIZE}px`}
          icon={<ReplaceIcon width={ICON_SIZE} height={ICON_SIZE} />}
          variant='icon-btn-widget-ctrl'
          onClick={onClick}
        />
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={onClick}
      width='82px'
      height={`32px`}
      _hover={{ backgroundColor: 'gray.100' }}
      gap='6px'
      p='6px'
      bg='white'
      borderRadius='4px'
      boxShadow='0px 0px 4px 0px #0000003D'
    >
      <ReplaceIcon width='20' height='20' />
      <Text fontSize={`${FONT_SIZE}px`} fontWeight='medium' color='font.500'>
        {label}
      </Text>
    </Button>
  );
};
